import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import portalClient from './portalClient';
import PortalLayout from './PortalLayout';
import { usePortalAuth } from './PortalAuthContext';
import { Card, Skeleton } from '../design-system';
import './Portal.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// The portal's front page answers one question: is there anything I need to do?
// Everything else is secondary, so "waiting on you" leads and is the only tile
// that ever turns amber.
function PortalHome() {
  const { client } = usePortalAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      portalClient.get('/api/portal/summary'),
      portalClient.get('/api/portal/requests'),
      portalClient.get('/api/portal/compliance'),
    ])
      .then(([s, r, c]) => {
        setSummary(s.data);
        setRequests(r.data);
        setFilings(c.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // What the firm is waiting on the client for — the actual to-do list.
  const needsYou = requests.filter((r) => !r.raisedByClient && r.status === 'open');
  const upcoming = filings
    .filter((f) => f.state !== 'filed')
    .slice(0, 5);

  if (loading) {
    return (
      <PortalLayout title="Home">
        <Card><Skeleton.Text lines={5} /></Card>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      title={`Hello${client?.name ? `, ${client.name}` : ''}`}
      subtitle="Everything your accountant needs from you, in one place."
    >
      <div className="portal-tiles">
        <button
          type="button"
          className={`portal-tile ${summary?.awaitingYou ? 'is-action' : ''}`}
          onClick={() => navigate('/portal/requests')}
        >
          <span className="portal-tile-num">{summary?.awaitingYou ?? 0}</span>
          <span className="portal-tile-label">Waiting on you</span>
        </button>
        <button
          type="button"
          className="portal-tile"
          onClick={() => navigate('/portal/requests')}
        >
          <span className="portal-tile-num">{summary?.unreadReplies ?? 0}</span>
          <span className="portal-tile-label">New replies</span>
        </button>
        <button type="button" className="portal-tile" onClick={() => navigate('/portal/documents')}>
          <span className="portal-tile-num">{summary?.documents ?? 0}</span>
          <span className="portal-tile-label">Documents</span>
        </button>
        <button type="button" className="portal-tile" onClick={() => navigate('/portal/compliance')}>
          <span className="portal-tile-num">{summary?.upcomingFilings ?? 0}</span>
          <span className="portal-tile-label">Upcoming filings</span>
        </button>
      </div>

      <div className="portal-columns">
        <Card padding="md">
          <div className="portal-card-head">
            <span className="portal-card-title">Your accountant needs</span>
            <Link to="/portal/requests" className="portal-link">See all</Link>
          </div>
          {needsYou.length === 0 ? (
            <p className="portal-muted">Nothing outstanding — you&apos;re all caught up.</p>
          ) : (
            <ul className="portal-list">
              {needsYou.slice(0, 5).map((r) => (
                <li key={r.id}>
                  <Link to={`/portal/requests/${r.id}`} className="portal-list-row">
                    <span className="portal-list-main">
                      {r.title}
                      {r.hasUnread && <span className="portal-dot" title="New reply" />}
                    </span>
                    <span className="portal-muted">{r.dueDate ? `by ${fmtDate(r.dueDate)}` : ''}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card padding="md">
          <div className="portal-card-head">
            <span className="portal-card-title">Upcoming filings</span>
            <Link to="/portal/compliance" className="portal-link">See all</Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="portal-muted">Nothing due right now.</p>
          ) : (
            <ul className="portal-list">
              {upcoming.map((f) => (
                <li key={f.id} className="portal-list-row">
                  <span className="portal-list-main">
                    {f.name}
                    <span className="portal-muted"> · {f.period}</span>
                  </span>
                  <span className={f.state === 'overdue' ? 'portal-overdue' : 'portal-muted'}>
                    {f.dueDate ? fmtDate(f.dueDate) : 'Date to be confirmed'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </PortalLayout>
  );
}

export default PortalHome;
